import React, { useState } from 'react'
import Head from 'next/head'
import { includes, invert } from 'lodash'
import { useRouter } from 'next/router'
import { useCookies } from 'react-cookie'

import { useComponentDidMount } from 'hooks/componentDidMount'

import { HUMAN_FORMAT, EDH_FORMATS, CardType, FORMAT, DEPRICATED_FORMAT_NAMES } from 'types/deck'
import { Color } from 'services/apiTypes/card.types'

import { store } from 'redux/store'
import { useActionless, useActions, useAppSelector } from 'redux/hooks'
import { useRightPanel } from 'hooks/useRightPanel'
import { selectCommanders, useCanEditDeck } from 'redux/deck/selectors'
import { setDeckStateAction } from 'redux/deck/actions'
import { SET_ACTIVE_STATE, TOGGLE_MODAL } from 'redux/active/actions/types'

import Icon from 'components/elements/Icon'
import DeckPrice from 'components/deckPage/header/DeckPrice'
import SaltSum from 'components/deckPage/header/SaltSum'
import Legality from 'components/deckPage/header/Legality'
import OtherUsers from 'components/deckPage/header/OtherUsers'
import AvatarCircle from 'components/user/AvatarCircle'
import RightPanelControls from 'components/deckPage/header/RightPanelControls'
import ShareAndSocailControls from 'components/deckPage/header/ShareAndSocialControls'
import PhatButton from 'components/formElements/PhatButton'
import Link from 'components/elements/Link'
import CardBanner from 'components/misc/CardBanner'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import PlaygroupData from 'components/deckPage/header/DeckHeader/PlaygroupData'
import CanlanderPoints from 'components/deckPage/header/CanlanderPoints'
import BracketPrompt from 'components/deckPage/header/calculatedBracketInfo/BracketPrompt'

import { getImageUrl } from 'utils/ImageUrl'
import { getDeckPriceAndInfo } from 'utils/calculateDeckPrice'
import { tagNameToUrl } from 'utils/deckTagAliases'
import { encodeUsernameRoute } from 'utils/encoding'
import { generateDeckAsQueryParam } from 'utils/deckAsQueryParam'
import { colorsToColorIdentityName } from 'utils/colorUtils'

const art = '/images/archidekt_small.webp'

import styles from './deckHeader.module.scss'

type Props = {
  className?: string
  containerClassName?: string
  hideControls?: boolean
}

const DeckHeader = ({ className = '', containerClassName = '', hideControls = false }: Props) => {
  const router = useRouter()

  const canEditDeck = useCanEditDeck()
  const componentDidMount = useComponentDidMount()

  const [{ tbId: userId }] = useCookies(['tbId'])

  const deckId = useAppSelector(state => state.deck.id)
  const deckName = useAppSelector(state => state.deck.name)
  const cardMap = useAppSelector(state => state.deck.cardMap)
  const commanders = useAppSelector(selectCommanders)
  const format = useAppSelector(state => state.deck.format)
  const owner = useAppSelector(state => state.deck.owner)
  const ownerid = useAppSelector(state => state.deck.ownerid)
  const ownerAvatar = useAppSelector(state => state.deck.ownerAvatar)
  const updatedAt = useAppSelector(state => state.deck.updatedAt)
  const priceSource = useAppSelector(state => state.active.priceSource)
  const parentFolder = useAppSelector(state => state.deck.parentFolder)
  const deckFormat = useAppSelector(state => state.deck.format)

  const privateDeck = useAppSelector(state => state.deck.private)
  const categories = useAppSelector(state => state.deck.categories)
  const featured = useAppSelector(state => state.deck.featured)
  const customFeatured = useAppSelector(state => state.deck.customFeatured)
  const snapshotMeta = useAppSelector(state => state.deck.snapshotMeta)
  const deckTags = useAppSelector(state => state.deck.deckTags)
  const cardPackage = useAppSelector(state => state.deck.cardPackage)

  const [setFeatured] = useActions(setDeckStateAction)
  const [rightPanelStatus, setRightPanelStatus] = useRightPanel()

  const [toggleModal, setActiveState] = useActionless(TOGGLE_MODAL, SET_ACTIVE_STATE)
  const [nameClicked, setNameClicked] = useState(false)

  const ownsDeck = Number(userId) === Number(ownerid)

  const deck: Array<any> = Object.values(cardMap)

  const handleOpenDeckTags = () => toggleModal({ modalName: 'deckTags', flag: true })

  const handleRouteSandboxToPlaytester = () => {
    const deckAsQueryParam = generateDeckAsQueryParam()

    window.open(`/playtester-v2/sandbox?deck=${deckAsQueryParam}`, '_ blank')
  }

  let faceCard: CardType | null = null
  let isCommanderFormat = includes(EDH_FORMATS, format)

  if (isCommanderFormat) faceCard = commanders[0] ? cardMap[commanders[0]] : null

  const { deckSize, estDeckCost, costWithoutBasics, excludingExactOwned, excludingAnyOwned } = getDeckPriceAndInfo(
    deck,
    categories,
    priceSource,
  )

  const humanFormat = invert(HUMAN_FORMAT)[format] || DEPRICATED_FORMAT_NAMES[format]
  const faceUrl = faceCard ? getImageUrl(faceCard.setCode, faceCard.uid, false, 'art_crop') : ''

  if (featured !== faceUrl) setFeatured({ featured: faceUrl })

  const isPackage = !!cardPackage
  const resourceType = isPackage ? 'Package' : 'Deck'

  const faceCardUrl = customFeatured || featured || art
  const playtesterButton =
    !!deckId && !snapshotMeta ? (
      <Link className={styles.playtesterLink} href={`/playtester-v2/${deckId}`}>
        <Icon name="play circle outline" /> Playtester
      </Link>
    ) : (
      <PhatButton onClick={handleRouteSandboxToPlaytester}>
        <Icon name="play circle outline" /> Playtester
      </PhatButton>
    )

  let seoDeckName = deckName

  // SEO Stuff for precon decks
  if (owner === 'Archidekt_Precons') {
    // @ts-ignore - Object.keys returns strings, it's fine
    const colors: Color[] = Object.keys(
      Object.values(cardMap).reduce(
        (acc, c) => {
          const colors = EDH_FORMATS.includes(format) ? c.colorIdentity : c.colors

          for (const color of colors) {
            acc[color] = true
          }

          return acc
        },
        {} as Record<Color, boolean>,
      ),
    )

    let colorName: string = colorsToColorIdentityName(colors)

    if (deckName.includes(colorName)) colorName = '' // Sometimes the deck includes the color identity name. Including it twice looks funny

    if (['White', 'Blue', 'Black', 'Red', 'Green'].includes(colorName)) colorName = `Mono-${colorName}`

    seoDeckName = `${deckName} - ${colorName} EDH Precon Decklist`
  }

  return (
    <CardBanner src={faceCardUrl} className={`${styles.heightenZIndex} ${className}`} allowOverflow>
      <div className={`${styles.container} ${containerClassName}`}>
        <Head>
          {/* THESE TWO ARE INTENTIONALLY LEFT OFF. They're enabled by the default headers component which is envoked via the <PageWrapper /> and <Headers /> components */}
          {/* <meta property="og:title" content={title} /> */}
          {/* <meta property="og:description" content={description} /> */}
          <meta property="og:url" content={router.asPath} />
          <meta property="og:type" content="article" />
          <meta property="article:published_time" content={updatedAt} />
          <meta property="article:author" content={owner === 'Archidekt_Precons' ? 'Archidekt Precons' : owner} />
          <meta property="og:image" content={faceCardUrl} />
          {/* <meta property="og:image:width" content="764" /> */}
          {/* <meta property="og:image:height" content="430" /> */}
          {/* <meta property="og:image:type" content="image/jpeg" /> */}
          <meta name="author" content={owner === 'Archidekt_Precons' ? 'Archidekt Precons' : owner} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:label1" content="Created by" />
          <meta name="twitter:data1" content={owner} />
          <meta name="twitter:label2" content="Format" />
          <meta name="twitter:data2" content={humanFormat} />
          <meta name="twitter:image" content={faceCardUrl} />
        </Head>

        {!hideControls && <ShareAndSocailControls className={styles.mobileSocialControl} />}

        <div className={styles.content}>
          <div className={styles.deckInfo}>
            <div
              title={seoDeckName}
              className={`${styles.deckNameContainer} ${nameClicked ? styles.nameClicked : ''}`}
              onClick={() => setNameClicked(!nameClicked)}>
              {privateDeck && <Icon name="lock" />}
              <span className={styles.deckName}>{seoDeckName}</span>
              {!snapshotMeta && ownsDeck && !hideControls && (
                <button onClick={() => toggleModal({ modalName: 'deckSettings', flag: true })}>
                  <Icon name="cog" />
                </button>
              )}
              {!!snapshotMeta && ' (Snapshot)'}
            </div>

            <div className={styles.row}>
              <div className={styles.deckSize}>
                {resourceType} Size: {deckSize}
              </div>

              {!isPackage && (
                <div className={styles.format}>
                  {humanFormat} <Legality />
                </div>
              )}

              {deckFormat === FORMAT.CANLANDER && <CanlanderPoints />}
            </div>

            <div className={styles.row}>
              <DeckPrice
                costWithoutBasics={costWithoutBasics}
                estDeckCost={estDeckCost}
                excludingExactOwned={excludingExactOwned}
                excludingAnyOwned={excludingAnyOwned}
              />
              <SaltSum />
              {deckFormat === FORMAT.EDH && componentDidMount && <BracketPrompt />}
              {isCommanderFormat && <PlaygroupData />}
            </div>

            {!isPackage && (
              <div className={styles.deckTags}>
                {deckTags.map((tag, index) => {
                  return (
                    <Link href={`/tags/${tagNameToUrl(tag.name || '')}`} key={index} className={styles.tag}>
                      {tag.name}
                    </Link>
                  )
                })}
                {!deckTags.length && <span className={styles.noTags}>No deck tags</span>}
                {ownsDeck && !hideControls && (
                  <button onClick={() => handleOpenDeckTags()}>
                    <Icon name="cog" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className={styles.userInfo}>
            <div className={styles.ownerInfo}>
              <AvatarCircle
                src={ownerAvatar || 'https://storage.googleapis.com/topdekt-user/avatars/default/avatar_colorless.svg'}
                username={owner}
              />
              <Link className={styles.username} href={`/u/${encodeUsernameRoute(owner)}`}>
                {owner}
              </Link>
            </div>

            <OtherUsers />
          </div>
        </div>

        {!hideControls && (
          <>
            <div className={styles.controls}>
              <ShareAndSocailControls className={styles.desktopSocialControl} />
              <div className={styles.mobilePlaytesterButton}>{playtesterButton}</div>

              <RightPanelControls />
            </div>

            <div className={`${styles.controls} ${styles.hideOnMobile}`}>
              <div className={styles.primaryActions}>
                {!canEditDeck ? (
                  <PhatButton
                    onClick={() => {
                      if (!userId) return router.push(`/login?redirectTo=${encodeURIComponent(router.asPath)}`)

                      toggleModal({ modalName: 'cloneModal', flag: true })
                    }}>
                    <Icon name="clone" /> Clone deck
                  </PhatButton>
                ) : (
                  <PhatButton onClick={() => toggleModal({ modalName: 'massEditModal', flag: true })}>
                    <Icon name="edit outline" /> Import cards
                  </PhatButton>
                )}
                <ArchidektDropdown
                  menuClassName={styles.deckControlsDropdown}
                  options={[
                    {
                      label: 'Export deck',
                      onClick: () => toggleModal({ modalName: 'export', flag: true }),
                      icon: 'download',
                    },
                    {
                      label: 'Clone deck',
                      onClick: () => toggleModal({ modalName: 'cloneModal', flag: true }),
                      icon: 'clone',
                      hidden: !userId,
                    },
                    { type: 'spacer' },
                    {
                      label: 'Card packages',
                      icon: 'cube',
                      onClick: () => toggleModal({ modalName: 'cardPackageModal', flag: true }),
                    },
                    {
                      label: 'Add deck to collection',
                      onClick: () => toggleModal({ modalName: 'addToCollection', flag: true }),
                      icon: 'plus circle',
                      hidden: !userId,
                    },
                    { type: 'spacer' },
                    {
                      label: 'Select all',
                      keybind: 'Ctrl + A',
                      onClick: () => {
                        const { cardMap } = store.getState().deck
                        const multiSelectedIds = Object.keys(cardMap).reduce((a, id) => ({ ...a, [id]: true }), {})

                        setActiveState({ multiSelectedIds })
                      },
                      icon: 'hand pointer outline',
                    },
                    {
                      label: 'Show keybinds',
                      keybind: '?',
                      onClick: () => toggleModal({ modalName: 'deckKeybinds', flag: true }),
                      icon: 'question circle outline',
                    },
                    { type: 'spacer' },
                    {
                      label: 'Optimize printings',
                      onClick: () => {
                        const { cardMap } = store.getState().deck
                        const multiSelectedIds = Object.keys(cardMap).reduce((a, id) => ({ ...a, [id]: true }), {})

                        setActiveState({ multiSelectedIds, optimizeCardOverlay: true })
                      },
                      icon: 'money bill alternate outline',
                    },
                    {
                      label: 'Basic land optimizer',
                      id: 'lands',
                      icon: 'fire',
                      onClick: () => setActiveState({ autofillBasicLandsOverlay: true }),
                    },
                    {
                      label: 'Deck stats',
                      icon: 'pie graph',
                      disabled: rightPanelStatus.locked,
                      onClick: () => setRightPanelStatus({ ...rightPanelStatus, open: true }),
                    },
                    {
                      label: 'Open containing folder',
                      to: `/folders/${parentFolder}`,
                      icon: 'folder open',
                      hidden: !ownsDeck,
                    },
                    { type: 'spacer' },
                    {
                      label: 'Deck history',
                      type: 'extras',
                      id: 'deck-history-options',
                      icon: 'history',
                      options: [
                        {
                          label: 'Recent deck changes',
                          onClick: () => toggleModal({ modalName: 'recentChanges', flag: true }),
                        },
                        {
                          label: 'Added / removed cards',
                          onClick: () => toggleModal({ modalName: 'addRemovedCards', flag: true }),
                        },
                        {
                          label: 'Deck snapshots',
                          onClick: () => toggleModal({ modalName: 'snapshots', flag: true }),
                        },
                        { type: 'spacer', hidden: !canEditDeck },
                        {
                          icon: 'add',
                          label: 'Create deck snapshot',
                          onClick: () => setActiveState({ modals: { createSnapshotStandalone: true } }),
                          hidden: !canEditDeck,
                        },
                      ],
                    },
                    {
                      label: 'Deck settings',
                      type: 'extras',
                      id: 'deck-settings-selector',
                      icon: 'cog',
                      options: [
                        {
                          label: 'Main deck settings',
                          onClick: () => toggleModal({ modalName: 'deckSettings', flag: true }),
                        },
                        {
                          label: 'Collaborators',
                          onClick: () => toggleModal({ modalName: 'collaborators', flag: true }),
                        },
                        {
                          label: 'Deck tags',
                          onClick: () => toggleModal({ modalName: 'deckTags', flag: true }),
                        },
                        {
                          label: 'Color tags',
                          onClick: () => toggleModal({ modalName: 'colorTags', flag: true }),
                        },
                        {
                          label: 'Categories',
                          onClick: () => toggleModal({ modalName: 'categories', flag: true }),
                        },
                      ],
                      hidden: !canEditDeck,
                    },
                    { type: 'spacer' },
                  ]}>
                  <PhatButton>
                    <Icon className={styles.extra} name="ellipsis vertical" /> Extras
                  </PhatButton>
                </ArchidektDropdown>
              </div>

              <div className={styles.row}>{playtesterButton}</div>
            </div>
          </>
        )}
      </div>
    </CardBanner>
  )
}

export default DeckHeader
